import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Bell, Mail, Trash2, Send, ArrowLeft, AlertCircle } from "lucide-react";

import AppShell from "../components/AppShell";
import { useAuth } from "../contexts/AuthContext";
import useModules from "../hooks/useModules";
import { ROLES } from "../lib/rbac";
import { api, formatApiError } from "../lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import {
    Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from "../components/ui/dialog";

const TYPES = [
    { value: "payroll_paid", label: "Payroll marked as Paid", description: "Sent when any user marks a payroll period as paid — includes PDF summary." },
];

function parseEmailsBlob(blob) {
    return (blob || "")
        .split(/[\n,;]+/)
        .map((s) => s.trim())
        .filter(Boolean);
}

function looksLikeEmail(s) {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(s);
}

export default function AdminNotifications() {
    const { user } = useAuth();
    const navigate = useNavigate();
    const { modules } = useModules();
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [editing, setEditing] = useState(null); // {notification_type, module_id, emailsBlob, id?}
    const [saving, setSaving] = useState(false);
    const [testingId, setTestingId] = useState(null);

    const isAdmin = user?.role === ROLES.KAAFI_ADMIN;

    const load = async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/admin/notification-settings");
            setItems(data);
        } catch (e) {
            toast.error(formatApiError(e));
        } finally { setLoading(false); }
    };
    useEffect(() => { if (isAdmin) load(); }, [isAdmin]);

    const moduleById = useMemo(() => Object.fromEntries(modules.map((m) => [m.id, m])), [modules]);

    const openEdit = (row) => {
        setEditing(row
            ? { id: row.id, notification_type: row.notification_type, module_id: row.module_id, emailsBlob: (row.emails || []).join("\n") }
            : { notification_type: "payroll_paid", module_id: null, emailsBlob: "" });
    };

    const closeEdit = () => setEditing(null);

    const submitEdit = async () => {
        if (!editing) return;
        const emails = parseEmailsBlob(editing.emailsBlob);
        const bad = emails.filter((e) => !looksLikeEmail(e));
        if (bad.length) {
            toast.error(`Invalid email(s): ${bad.slice(0, 3).join(", ")}${bad.length > 3 ? "…" : ""}`);
            return;
        }
        setSaving(true);
        try {
            await api.post("/admin/notification-settings", {
                notification_type: editing.notification_type,
                module_id: editing.module_id || null,
                emails,
            });
            toast.success("Notification recipients saved");
            closeEdit();
            load();
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setSaving(false); }
    };

    const doDelete = async (row) => {
        if (!window.confirm(`Delete recipients for ${row.notification_type} · ${moduleById[row.module_id]?.name || "All modules"}?`)) return;
        try {
            await api.delete(`/admin/notification-settings/${row.id}`);
            toast.success("Recipients removed");
            load();
        } catch (e) { toast.error(formatApiError(e)); }
    };

    const doTest = async (row) => {
        setTestingId(row.id);
        try {
            const { data } = await api.post("/admin/notification-settings/test", {
                notification_type: row.notification_type,
                module_id: row.module_id,
            });
            const r = data.result || {};
            if (r.sent) {
                toast.success(`Test sent to ${data.recipients.length} recipient(s) · id=${r.id?.slice(0, 8)}…`);
            } else if (r.reason === "no_api_key") {
                toast.warning(`Test skipped: RESEND_API_KEY not configured. ${data.recipients.length} recipient(s) would have been notified.`);
            } else {
                toast.error(`Test failed: ${r.reason || "unknown"}`);
            }
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setTestingId(null); }
    };

    if (!isAdmin) {
        return (
            <AppShell>
                <div className="p-8">
                    <Card>
                        <CardContent className="p-8 text-center">
                            <AlertCircle className="mx-auto mb-3 text-rose-600" size={32} />
                            <div className="font-semibold asl-navy-text">403 — System Admin only</div>
                            <div className="text-sm text-slate-500 mt-1">You don&apos;t have access to notification settings.</div>
                        </CardContent>
                    </Card>
                </div>
            </AppShell>
        );
    }

    return (
        <AppShell>
            <div className="p-6 space-y-6" data-testid="admin-notifications-page">
                <div className="flex items-center gap-3">
                    <Button variant="ghost" size="sm" onClick={() => navigate("/admin/settings")} data-testid="admin-notif-back">
                        <ArrowLeft size={16} className="mr-1" /> System Settings
                    </Button>
                </div>

                <div>
                    <div className="flex items-center gap-2">
                        <Bell className="asl-navy-text" size={22} />
                        <h1 className="text-2xl font-bold asl-navy-text">Notification Recipients</h1>
                    </div>
                    <p className="text-sm text-slate-600 mt-1 max-w-3xl">
                        Configure who receives automated email notifications when specific events happen in the platform.
                        Global recipients (module scope: <em>All modules</em>) always receive the email;
                        module-specific recipients are added on top of the global list. Maximum 20 recipients per event/module.
                    </p>
                </div>

                <Card>
                    <CardHeader className="flex flex-row items-center justify-between pb-3">
                        <CardTitle className="text-base">Configured recipient lists</CardTitle>
                        <Button size="sm" onClick={() => openEdit(null)} data-testid="admin-notif-add">
                            <Mail size={14} className="mr-1" /> Add recipient list
                        </Button>
                    </CardHeader>
                    <CardContent className="p-0">
                        <table className="w-full text-sm">
                            <thead className="bg-slate-50 border-y">
                                <tr>
                                    <th className="text-left px-4 py-2 font-medium text-slate-600 uppercase tracking-wider text-xs">Event</th>
                                    <th className="text-left px-4 py-2 font-medium text-slate-600 uppercase tracking-wider text-xs">Scope</th>
                                    <th className="text-left px-4 py-2 font-medium text-slate-600 uppercase tracking-wider text-xs">Recipients</th>
                                    <th className="text-right px-4 py-2 font-medium text-slate-600 uppercase tracking-wider text-xs">Actions</th>
                                </tr>
                            </thead>
                            <tbody data-testid="admin-notif-table-body">
                                {loading && (
                                    <tr><td colSpan={4} className="text-center p-6 text-slate-400">Loading…</td></tr>
                                )}
                                {!loading && items.length === 0 && (
                                    <tr>
                                        <td colSpan={4} className="p-6 text-center text-slate-500" data-testid="admin-notif-empty">
                                            No recipient lists configured yet. Click <strong>Add recipient list</strong> to add one.
                                        </td>
                                    </tr>
                                )}
                                {items.map((row) => {
                                    const typeDef = TYPES.find((t) => t.value === row.notification_type);
                                    const scope = row.module_id
                                        ? (moduleById[row.module_id]?.name || row.module_id)
                                        : "All modules";
                                    return (
                                        <tr key={row.id} className="border-b" data-testid={`admin-notif-row-${row.id}`}>
                                            <td className="px-4 py-3">
                                                <div className="font-medium">{typeDef?.label || row.notification_type}</div>
                                                <div className="text-xs text-slate-500">{typeDef?.description}</div>
                                            </td>
                                            <td className="px-4 py-3">
                                                <Badge variant={row.module_id ? "outline" : "default"} className="text-xs">
                                                    {scope}
                                                </Badge>
                                            </td>
                                            <td className="px-4 py-3">
                                                <div className="flex flex-wrap gap-1" data-testid={`admin-notif-emails-${row.id}`}>
                                                    {(row.emails || []).map((e) => (
                                                        <span key={e} className="inline-flex items-center gap-1 px-2 py-0.5 bg-slate-100 border border-slate-200 rounded text-xs">
                                                            <Mail size={11} className="text-slate-500" /> {e}
                                                        </span>
                                                    ))}
                                                </div>
                                            </td>
                                            <td className="px-4 py-3 text-right whitespace-nowrap">
                                                <Button size="sm" variant="outline" className="mr-2" onClick={() => doTest(row)}
                                                        disabled={testingId === row.id}
                                                        data-testid={`admin-notif-test-${row.id}`}>
                                                    <Send size={13} className="mr-1" />
                                                    {testingId === row.id ? "Sending…" : "Test"}
                                                </Button>
                                                <Button size="sm" variant="outline" className="mr-2" onClick={() => openEdit(row)}
                                                        data-testid={`admin-notif-edit-${row.id}`}>
                                                    Edit
                                                </Button>
                                                <Button size="sm" variant="ghost" onClick={() => doDelete(row)}
                                                        data-testid={`admin-notif-delete-${row.id}`}>
                                                    <Trash2 size={13} className="text-rose-600" />
                                                </Button>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </CardContent>
                </Card>

                <div className="text-xs text-slate-500 max-w-3xl">
                    <strong>Note:</strong> If the Resend API key isn&apos;t configured on the server yet, test sends will report
                    &ldquo;skipped — no API key&rdquo; and real events will log a warning but never fail the underlying flow.
                    Set <code>RESEND_API_KEY</code> and <code>RESEND_FROM_EMAIL</code> in the backend environment to enable delivery.
                </div>
            </div>

            {editing && (
                <Dialog open onOpenChange={(o) => !o && closeEdit()}>
                    <DialogContent className="max-w-lg" data-testid="admin-notif-dialog">
                        <DialogHeader>
                            <DialogTitle>{editing.id ? "Edit recipient list" : "Add recipient list"}</DialogTitle>
                        </DialogHeader>
                        <div className="space-y-4 py-2">
                            <div>
                                <Label>Event</Label>
                                <Select
                                    value={editing.notification_type}
                                    onValueChange={(v) => setEditing({ ...editing, notification_type: v })}
                                    disabled={!!editing.id}
                                >
                                    <SelectTrigger data-testid="admin-notif-form-type"><SelectValue /></SelectTrigger>
                                    <SelectContent>
                                        {TYPES.map((t) => (
                                            <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                                <p className="text-xs text-slate-500 mt-1">
                                    {TYPES.find((t) => t.value === editing.notification_type)?.description}
                                </p>
                            </div>
                            <div>
                                <Label>Scope</Label>
                                <Select
                                    value={editing.module_id || "__all__"}
                                    onValueChange={(v) => setEditing({ ...editing, module_id: v === "__all__" ? null : v })}
                                    disabled={!!editing.id}
                                >
                                    <SelectTrigger data-testid="admin-notif-form-scope"><SelectValue /></SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="__all__">All modules (global)</SelectItem>
                                        {modules.map((m) => (
                                            <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                                <p className="text-xs text-slate-500 mt-1">
                                    Global recipients receive every event; module-specific recipients are unioned in.
                                </p>
                            </div>
                            <div>
                                <Label>Email addresses <span className="text-slate-400 font-normal">(one per line, max 20)</span></Label>
                                <Textarea
                                    rows={6}
                                    className="mono text-sm"
                                    placeholder="hr@kaafi.com&#10;finance@aslwms.com&#10;manager@example.com"
                                    value={editing.emailsBlob}
                                    onChange={(e) => setEditing({ ...editing, emailsBlob: e.target.value })}
                                    data-testid="admin-notif-form-emails"
                                />
                                <p className="text-xs text-slate-500 mt-1">
                                    Duplicates and invalid entries are dropped on save. Case-insensitive.
                                </p>
                            </div>
                        </div>
                        <DialogFooter>
                            <Button variant="outline" onClick={closeEdit} data-testid="admin-notif-form-cancel">Cancel</Button>
                            <Button onClick={submitEdit} disabled={saving} data-testid="admin-notif-form-save">
                                {saving ? "Saving…" : "Save"}
                            </Button>
                        </DialogFooter>
                    </DialogContent>
                </Dialog>
            )}
        </AppShell>
    );
}
